// frontend/src/pages/UsersPage.jsx
import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db, auth } from '../config/firebase';
import AdminLayout from './AdminLayout';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const HIDDEN_EMAILS = [
  'geraldmuthui@gmail.com',
  'jeprosservices@gmail.com',
]; // Superadmin accounts — hidden from list

const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [currentUserUid, setCurrentUserUid] = useState('');
  const [currentUserRole, setCurrentUserRole] = useState(''); //  Track user role
  
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    displayName: '',
    role: 'member',
    memberId: ''
  });
  const [creating, setCreating] = useState(false);
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [memberSearch, setMemberSearch] = useState('');
  const [showMemberDropdown, setShowMemberDropdown] = useState(false);

  const [busyApproveFor, setBusyApproveFor] = useState(null);
  const [busyRejectFor, setBusyRejectFor] = useState(null);
  const [busyRoleFor, setBusyRoleFor] = useState(null);
  const [busyDeleteFor, setBusyDeleteFor] = useState(null);
  const [linkingFor, setLinkingFor] = useState(null); // uid of user being linked
  const [linkModal, setLinkModal] = useState({ show: false, userId: null, currentMemberId: '' });
  const [linkModalSelected, setLinkModalSelected] = useState('');
  const [linkModalSearch, setLinkModalSearch] = useState('');
  const [lastLoginSort, setLastLoginSort] = useState('desc'); // 'desc' = most recent first, 'asc' = oldest first

  useEffect(() => {
    // Get current logged-in user's UID and role
    try {
      const storedUser = localStorage.getItem('hopeSaccoUser');
      if (storedUser) {
        const userData = JSON.parse(storedUser);
        setCurrentUserUid(userData.uid || '');
        setCurrentUserRole(userData.role || ''); //  Get user role
      }
    } catch (err) {
      console.error('Error getting current user:', err);
    }

    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError('');
      
      const usersRef = collection(db, 'users');
      const snapshot = await getDocs(usersRef);
      
      const usersData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      //  Filter out superuser email from the list
      const filteredUsers = usersData.filter(user =>
        !HIDDEN_EMAILS.includes((user.email || '').toLowerCase()) &&
        !HIDDEN_EMAILS.includes((user.emailLower || '').toLowerCase())
      );

      setUsers(filteredUsers);
    } catch (err) {
      console.error('Error loading users:', err);
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const loadMembers = async () => {
    try {
      setMembersLoading(true);
      const snap = await getDocs(collection(db, 'members'));
      const list = snap.docs
        .map(d => ({ memberId: d.data().memberId, fullName: d.data().personalInfo?.fullName || d.data().personalInfo?.firstName || '' }))
        .filter(m => m.memberId)
        .sort((a, b) => a.memberId.localeCompare(b.memberId));
      setMembers(list);
    } catch (err) {
      console.error('Error loading members:', err);
    } finally {
      setMembersLoading(false);
    }
  };

  const approveUser = async (userId) => {
    if (!window.confirm('Approve this user? They will be able to sign in.')) {
      return;
    }

    try {
      setBusyApproveFor(userId);
      setError('');
      setSuccessMessage('');

      const userDoc = doc(db, 'users', userId);
      await updateDoc(userDoc, {
        approved: true,
        isActive: true,
        status: 'active',
        approvedAt: Date.now()
      });

      setSuccessMessage('User approved successfully');
      await loadUsers();
    } catch (err) {
      console.error('Error approving user:', err);
      setError('Failed to approve user');
    } finally {
      setBusyApproveFor(null);
    }
  };

  const rejectUser = async (userId, userEmail) => {
    const typed = window.prompt(
      `Type DELETE to permanently remove this user:\n${userEmail}`
    );
    
    if (typed === null) return;
    
    if (typed.trim().toUpperCase() !== 'DELETE') {
      alert('Deletion cancelled.');
      return;
    }

    try {
      setBusyRejectFor(userId);
      setError('');
      setSuccessMessage('');

      await deleteDoc(doc(db, 'users', userId));
      
      setSuccessMessage('User rejected and removed');
      await loadUsers();
    } catch (err) {
      console.error('Error rejecting user:', err);
      setError('Failed to reject user');
    } finally {
      setBusyRejectFor(null);
    }
  };

  const changeUserRole = async (userId, newRole) => {
    try {
      setBusyRoleFor(userId);
      setError('');
      setSuccessMessage('');

      const userDoc = doc(db, 'users', userId);
      await updateDoc(userDoc, {
        role: newRole,
        updatedAt: Date.now()
      });

      setSuccessMessage('User role updated');
      await loadUsers();
    } catch (err) {
      console.error('Error changing role:', err);
      setError('Failed to change user role');
    } finally {
      setBusyRoleFor(null);
    }
  };

  const deleteUser = async (userId, userEmail) => {
    //  Check if current user is admin
    if (currentUserRole !== 'admin') {
      alert('Only administrators can delete users.');
      return;
    }

    //  Prevent self-deletion (even for admins)
    if (userId === currentUserUid) {
      alert('You cannot delete your own account while signed in.');
      return;
    }

    const typed = window.prompt(
      `Type DELETE to permanently remove this user:\n${userEmail}`
    );
    
    if (typed === null) return;
    
    if (typed.trim().toUpperCase() !== 'DELETE') {
      alert('Deletion cancelled.');
      return;
    }

    try {
      setBusyDeleteFor(userId);
      setError('');
      setSuccessMessage('');

      const token = localStorage.getItem('hopeSaccoToken');
      const response = await fetch(`${API_URL}/users/${userId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to delete user');

      setSuccessMessage('User deleted successfully');
      await loadUsers();
    } catch (err) {
      console.error('Error deleting user:', err);
      setError('Failed to delete user');
    } finally {
      setBusyDeleteFor(null);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    
    if (!newUser.email || !newUser.password || !newUser.displayName) {
      alert('All fields are required');
      return;
    }

    if (newUser.password.length < 6) {
      alert('Password must be at least 6 characters');
      return;
    }

    if (newUser.role === 'member' && !newUser.memberId.trim()) {
      setError('Member ID is required when creating a member account');
      return;
    }

    try {
      setCreating(true);
      setError('');
      setSuccessMessage('');

      const idToken = await auth.currentUser?.getIdToken(true);

      const response = await fetch(`${API_URL}/users/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({
          email: newUser.email.trim(),
          password: newUser.password,
          displayName: newUser.displayName.trim(),
          role: newUser.role,
          memberId: newUser.role === 'member' ? newUser.memberId.trim() : undefined
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create user');
      }

      setSuccessMessage(`User created successfully: ${newUser.email}`);
      setNewUser({ email: '', password: '', displayName: '', role: 'member', memberId: '' });
      setShowCreateForm(false);
      await loadUsers();
    } catch (err) {
      console.error('Error creating user:', err);
      setError(err.message || 'Failed to create user');
    } finally {
      setCreating(false);
    }
  };

  const linkMemberId = async (userId, currentMemberId) => {
    if (members.length === 0) await loadMembers();
    setLinkModalSelected(currentMemberId || '');
    setLinkModalSearch('');
    setLinkModal({ show: true, userId, currentMemberId: currentMemberId || '' });
  };

  const confirmLinkMemberId = async () => {
    const memberId = linkModalSelected.trim();
    if (!memberId) { alert('Please select a member.'); return; }
    try {
      setLinkingFor(linkModal.userId);
      setError('');
      setSuccessMessage('');
      await updateDoc(doc(db, 'users', linkModal.userId), {
        memberId,
        profile: { memberId },
        updatedAt: Date.now()
      });
      setSuccessMessage(`Member ID "${memberId}" linked successfully.`);
      setLinkModal({ show: false, userId: null, currentMemberId: '' });
      await loadUsers();
    } catch (err) {
      console.error('Error linking member ID:', err);
      setError('Failed to link Member ID. Please try again.');
    } finally {
      setLinkingFor(null);
    }
  };

  const pendingUsers = users.filter(u => u.approved === false || u.status === 'pending');
  const approvedUsers = users.filter(u => u.approved !== false && u.status !== 'pending');

  const getLastLoginTs = (user) => {
    const ts = user.lastLoginAt || (user.lastLogin?._seconds ? user.lastLogin._seconds * 1000 : null);
    return ts || null;
  };

  const sortedApprovedUsers = [...approvedUsers].sort((a, b) => {
    const tsA = getLastLoginTs(a);
    const tsB = getLastLoginTs(b);
    // Never-logged-in always last regardless of sort direction
    if (!tsA && !tsB) return 0;
    if (!tsA) return 1;
    if (!tsB) return -1;
    return lastLoginSort === 'desc' ? tsB - tsA : tsA - tsB;
  });

  //  Check if current user is admin
  const isAdmin = currentUserRole === 'admin';

  return (
    <AdminLayout 
      activePage="users" 
      pageTitle="User Management"
      breadcrumbs={[
        { label: 'Admin', link: '/admin/dashboard' },
        { label: 'Users' }
      ]}
    >
      <div className="page-content">
        {/* Success/Error Messages */}
        {successMessage && (
          <div className="alert alert-success" style={{ marginBottom: '1rem' }}>
            <span>{successMessage}</span>
            <button onClick={() => setSuccessMessage('')} style={{ 
              background: 'none', 
              border: 'none', 
              color: 'inherit', 
              cursor: 'pointer',
              marginLeft: 'auto',
              fontSize: '1.2rem'
            }}>✕</button>
          </div>
        )}
        
        {error && (
          <div className="alert alert-error" style={{ marginBottom: '1rem' }}>
            <span>{error}</span>
            <button onClick={() => setError('')} style={{ 
              background: 'none', 
              border: 'none', 
              color: 'inherit', 
              cursor: 'pointer',
              marginLeft: 'auto',
              fontSize: '1.2rem'
            }}>✕</button>
          </div>
        )}

        {/* Create User Section */}
        <div className="card" style={{ marginBottom: '2rem' }}>
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2 className="card-title">Create New User</h2>
            <button
              className="btn btn-primary"
              onClick={() => {
                const opening = !showCreateForm;
                setShowCreateForm(opening);
                if (opening && members.length === 0) loadMembers();
              }}
            >
              {showCreateForm ? 'Cancel' : '+ Create User'}
            </button>
          </div>

          {showCreateForm && (
            <form onSubmit={handleCreateUser} className="card-body">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
                <div className="form-group">
                  <label>Display Name *</label>
                  <input
                    type="text"
                    value={newUser.displayName}
                    onChange={(e) => setNewUser({ ...newUser, displayName: e.target.value })}
                    placeholder="John Doe"
                    disabled={creating}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    value={newUser.email}
                    onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                    placeholder="user@example.com"
                    disabled={creating}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Password *</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={newUser.password}
                      onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                      placeholder="Minimum 6 characters"
                      disabled={creating}
                      minLength={6}
                      required
                      style={{ paddingRight: '40px', width: '100%', boxSizing: 'border-box' }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      style={{
                        position: 'absolute', right: '10px', top: '50%',
                        transform: 'translateY(-50%)', background: 'none',
                        border: 'none', cursor: 'pointer', fontSize: '16px', padding: 0
                      }}
                    >
                      {showPassword ? '🙈' : '👁️'}
                    </button>
                  </div>
                </div>

                <div className="form-group">
                  <label>Role *</label>
                  <select
                    value={newUser.role}
                    onChange={(e) => {
                      const role = e.target.value;
                      setNewUser({ ...newUser, role, memberId: '' });
                      if (role === 'member' && members.length === 0) loadMembers();
                    }}
                    disabled={creating}
                  >
                    <option value="member">Member</option>
                    <option value="official">Official</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>

                {newUser.role === 'member' && (
                  <div className="form-group" style={{ position: 'relative' }}>
                    <label>Member ID *</label>
                    {membersLoading ? (
                      <input type="text" disabled value="Loading members..." />
                    ) : (
                      <>
                        <input
                          type="text"
                          value={memberSearch || (newUser.memberId ? `${newUser.memberId}${members.find(m => m.memberId === newUser.memberId)?.fullName ? ` — ${members.find(m => m.memberId === newUser.memberId).fullName}` : ''}` : '')}
                          onChange={(e) => {
                            setMemberSearch(e.target.value);
                            setNewUser({ ...newUser, memberId: '' });
                            setShowMemberDropdown(true);
                          }}
                          onFocus={() => setShowMemberDropdown(true)}
                          onBlur={() => setTimeout(() => setShowMemberDropdown(false), 200)}
                          placeholder="Type name or ID to search..."
                          disabled={creating}
                          autoComplete="off"
                        />
                        {showMemberDropdown && memberSearch.length > 0 && (
                          <div style={{
                            position: 'absolute', top: '100%', left: 0, right: 0,
                            background: '#fff', border: '1px solid #ddd',
                            borderRadius: '4px', maxHeight: '200px', overflowY: 'auto',
                            zIndex: 1000, boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                          }}>
                            {members
                              .filter(m => {
                                const q = memberSearch.toLowerCase();
                                return m.memberId.toLowerCase().includes(q) || m.fullName.toLowerCase().includes(q);
                              })
                              .slice(0, 20)
                              .map(m => (
                                <div
                                  key={m.memberId}
                                  onMouseDown={() => {
                                    setNewUser({ ...newUser, memberId: m.memberId });
                                    setMemberSearch('');
                                    setShowMemberDropdown(false);
                                  }}
                                  style={{
                                    padding: '8px 12px', cursor: 'pointer',
                                    borderBottom: '1px solid #f0f0f0', fontSize: '14px'
                                  }}
                                  onMouseOver={e => e.currentTarget.style.background = '#f0f4ff'}
                                  onMouseOut={e => e.currentTarget.style.background = '#fff'}
                                >
                                  <strong>{m.memberId}</strong>{m.fullName ? ` — ${m.fullName}` : ''}
                                </div>
                              ))}
                            {members.filter(m => {
                              const q = memberSearch.toLowerCase();
                              return m.memberId.toLowerCase().includes(q) || m.fullName.toLowerCase().includes(q);
                            }).length === 0 && (
                              <div style={{ padding: '8px 12px', color: '#999', fontSize: '14px' }}>No members found</div>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>

              <div style={{ marginTop: '1rem' }}>
                <button type="submit" className="btn btn-primary" disabled={creating}>
                  {creating ? 'Creating...' : 'Create User'}
                </button>
              </div>

              <p style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: '#666' }}>
                Tip: Users created here will be approved immediately and can sign in right away.
              </p>
            </form>
          )}
        </div>

        {/* Pending Approvals */}
        <div className="card" style={{ marginBottom: '2rem' }}>
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2 className="card-title">Pending Approvals</h2>
            <span style={{ 
              display: 'inline-block', 
              padding: '0.25rem 0.75rem', 
              borderRadius: '12px', 
              fontSize: '0.75rem', 
              fontWeight: 600,
              backgroundColor: '#fff3cd',
              color: '#856404'
            }}>{pendingUsers.length} waiting</span>
          </div>

          {loading ? (
            <div className="card-body" style={{ textAlign: 'center' }}>
              <div className="spinner"></div>
              <p>Loading users...</p>
            </div>
          ) : pendingUsers.length === 0 ? (
            <div className="card-body" style={{ textAlign: 'center', color: '#666' }}>
              No pending approvals
            </div>
          ) : (
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Requested At</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingUsers.map(user => (
                    <tr key={user.id}>
                      <td>{user.displayName || '-'}</td>
                      <td>{user.email}</td>
                      <td>
                        <span style={{ 
                          display: 'inline-block', 
                          padding: '0.25rem 0.75rem', 
                          borderRadius: '12px', 
                          fontSize: '0.75rem', 
                          fontWeight: 600,
                          backgroundColor: '#fff3cd',
                          color: '#856404'
                        }}>{user.role}</span>
                      </td>
                      <td>
                        {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : '-'}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <button
                            className="btn btn-sm btn-success"
                            onClick={() => approveUser(user.id)}
                            disabled={busyApproveFor === user.id}
                            title="Approve user"
                          >
                            {busyApproveFor === user.id ? '...' : 'Approve'}
                          </button>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => rejectUser(user.id, user.email)}
                            disabled={busyRejectFor === user.id}
                            title="Reject and delete"
                          >
                            {busyRejectFor === user.id ? '...' : 'Reject'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* All Users */}
        <div className="card">
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2 className="card-title">All Users</h2>
            <span style={{ 
              display: 'inline-block', 
              padding: '0.25rem 0.75rem', 
              borderRadius: '12px', 
              fontSize: '0.75rem', 
              fontWeight: 600,
              backgroundColor: '#d4edda',
              color: '#155724'
            }}>{approvedUsers.length} users</span>
          </div>

          {loading ? (
            <div className="card-body" style={{ textAlign: 'center' }}>
              <div className="spinner"></div>
              <p>Loading users...</p>
            </div>
          ) : approvedUsers.length === 0 ? (
            <div className="card-body" style={{ textAlign: 'center', color: '#666' }}>
              No users found
            </div>
          ) : (
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th
                      onClick={() => setLastLoginSort(s => s === 'desc' ? 'asc' : 'desc')}
                      style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                      title="Click to toggle sort order"
                    >
                      Last Login {lastLoginSort === 'desc' ? '↓' : '↑'}
                    </th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedApprovedUsers.map(user => (
                    <tr key={user.id}>
                      <td>{user.displayName || '-'}</td>
                      <td>{user.email}</td>
                      <td>
                        <select
                          value={user.role || 'member'}
                          onChange={(e) => changeUserRole(user.id, e.target.value)}
                          disabled={busyRoleFor === user.id}
                          style={{ 
                            padding: '0.25rem 0.5rem', 
                            border: '1px solid #ddd', 
                            borderRadius: '4px', 
                            fontSize: '0.875rem' 
                          }}
                        >
                          <option value="member">Member</option>
                          <option value="official">Official</option>
                          <option value="admin">Admin</option>
                        </select>
                      </td>
                      <td>
                        <span style={{ 
                          display: 'inline-block', 
                          padding: '0.25rem 0.75rem', 
                          borderRadius: '12px', 
                          fontSize: '0.75rem', 
                          fontWeight: 600,
                          backgroundColor: user.isActive ? '#d4edda' : '#fff3cd',
                          color: user.isActive ? '#155724' : '#856404'
                        }}>
                          {user.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        {(() => {
                          const ts = user.lastLoginAt || (user.lastLogin?._seconds ? user.lastLogin._seconds * 1000 : null);
                          return ts
                            ? new Date(ts).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
                            : '—';
                        })()}
                      </td>
                      <td>
                        {/* Show delete button ONLY if:
                            1. Current user is admin AND
                            2. This is not the current user's own row */}
                        <button
                            className="btn btn-sm btn-secondary"
                            onClick={() => linkMemberId(user.id, user.memberId)}
                            disabled={linkingFor === user.id}
                            title={user.memberId ? `Linked: ${user.memberId}` : 'Link a Member ID'}
                            style={{ marginRight: '0.4rem' }}
                          >
                            {linkingFor === user.id ? '...' : user.memberId ? `ID: ${user.memberId}` : 'Link Member'}
                          </button>
                        {isAdmin && user.id !== currentUserUid && (
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => deleteUser(user.id, user.email)}
                            disabled={busyDeleteFor === user.id}
                            title="Delete user"
                          >
                            {busyDeleteFor === user.id ? '...' : 'Delete'}
                          </button>
                        )}
                        {user.id === currentUserUid && (
                          <span style={{ fontSize: '0.875rem', color: '#666' }}>
                            (You)
                          </span>
                        )}
                        {!isAdmin && user.id !== currentUserUid && (
                          <span style={{ fontSize: '0.875rem', color: '#999' }}>
                            Admin only
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Link Member Modal */}
      {linkModal.show && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 10, padding: '1.75rem', width: 420, maxWidth: '95vw', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0, fontSize: '1.1rem' }}>🔗 Link Member to User</h3>
              <button onClick={() => setLinkModal({ show: false, userId: null, currentMemberId: '' })}
                style={{ background: 'none', border: 'none', fontSize: '1.3rem', cursor: 'pointer', color: '#666' }}>✕</button>
            </div>

            {linkModal.currentMemberId && (
              <p style={{ margin: '0 0 0.75rem', fontSize: '0.85rem', color: '#555' }}>
                Currently linked: <strong>{linkModal.currentMemberId}</strong>
              </p>
            )}

            <div className="form-group" style={{ marginBottom: '0.75rem' }}>
              <label style={{ fontSize: '0.85rem', marginBottom: 4, display: 'block' }}>Search member</label>
              <input
                type="text"
                value={linkModalSearch}
                onChange={(e) => setLinkModalSearch(e.target.value)}
                placeholder="Type name or ID to filter..."
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: 6 }}
                autoFocus
              />
            </div>

            <div className="form-group" style={{ marginBottom: '1rem' }}>
              <label style={{ fontSize: '0.85rem', marginBottom: 4, display: 'block' }}>Select Member *</label>
              {membersLoading ? (
                <p style={{ color: '#888', fontSize: '0.875rem' }}>Loading members...</p>
              ) : (
                <select
                  value={linkModalSelected}
                  onChange={(e) => setLinkModalSelected(e.target.value)}
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: 6, fontSize: '0.9rem' }}
                  size={Math.min(8, members.filter(m =>
                    !linkModalSearch || m.memberId.toLowerCase().includes(linkModalSearch.toLowerCase()) || m.fullName.toLowerCase().includes(linkModalSearch.toLowerCase())
                  ).length + 1)}
                >
                  <option value="">-- Select Member --</option>
                  {members
                    .filter(m => !linkModalSearch || m.memberId.toLowerCase().includes(linkModalSearch.toLowerCase()) || m.fullName.toLowerCase().includes(linkModalSearch.toLowerCase()))
                    .map(m => (
                      <option key={m.memberId} value={m.memberId}>
                        {m.memberId} — {m.fullName || '(no name)'}
                      </option>
                    ))}
                </select>
              )}
            </div>

            {linkModalSelected && (
              <p style={{ fontSize: '0.85rem', color: '#1a6b3c', fontWeight: 600, margin: '0 0 0.75rem' }}>
                Selected: {linkModalSelected}
              </p>
            )}

            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary"
                onClick={() => setLinkModal({ show: false, userId: null, currentMemberId: '' })}>
                Cancel
              </button>
              <button className="btn btn-primary" disabled={!linkModalSelected || linkingFor === linkModal.userId}
                onClick={confirmLinkMemberId}>
                {linkingFor === linkModal.userId ? 'Saving...' : '🔗 Link Member'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default UsersPage;